import React from "react";
import { Helmet } from "react-helmet";
import { navigate } from "hookrouter";
import { colors, serif, sans, eyebrow } from "../../theme/brand";

const clients = ["Change CX", "Clous Mode", "Artific", "GrandVision", "Booming Jobs", "Beecot", "PIDZ", "VodafoneZiggo"];

const stats = [
  { value: "11+ jaar", label: "ervaring met bedrijfskritische softwareplatformen" },
  { value: "5000+", label: "klantgesprekken per dag geanalyseerd door AI-pipelines die we bouwden" },
  { value: "€100M+", label: "jaaromzet via het commerceplatform waarvan we betalingen & orders dragen" },
  { value: "GCP · Azure · AWS", label: "platformen vanaf nul opgezet en in productie beheerd" },
];

const helpItems = [
  {
    n: "01",
    title: "Volledige verantwoordelijkheid",
    body: "Eén aanspreekpunt dat eigenaarschap neemt over het geheel — architectuur, infrastructuur, code én het resultaat in productie.",
  },
  {
    n: "02",
    title: "Brug tussen business en techniek",
    body: "We schuiven aan bij directie, product, sales en klanten, vertalen vraagstukken naar haalbare oplossingen en blijven zelf meebouwen.",
  },
  {
    n: "03",
    title: "Het juiste team, per project",
    body: "We stellen per opdracht het team samen dat past, zetten de standaarden neer en coachen het tot het platform zelfstandig verder kan.",
  },
  {
    n: "04",
    title: "Van strategie tot beheer",
    body: "Architectuur, bouw, oplevering en doorlopend beheer — onder één dak, zonder overdrachtsgaten.",
  },
];

const services = [
  { title: "AI- & LLM-platformen", body: "AI-pipelines, evaluatie en observability die in productie blijven werken." },
  { title: "Interim CTO & tech lead", body: "Technische strategie en architectuur, met de business aan tafel." },
  { title: "Platform engineering & cloud", body: "Infrastructuur als code op GCP, Azure en AWS, vanaf nul." },
  { title: "Backend, API's & integraties", body: "Microservices, GraphQL, betalingen en koppelingen die kritieke processen dragen." },
  { title: "Modernisering & teams opbouwen", body: "Van legacy-monoliet naar cloud-native, met een team dat het zelf kan dragen." },
];

const cases = [
  {
    client: "Change CX",
    title: "Een AI-platform dat klantgesprekken begrijpt",
    body: "Architectuur en bouw van een cloud-native AI-platform dat klantcontact via audio, chat en e-mail transcribeert en analyseert. Van de single-tenant GCP-infrastructuur tot LLM-pipelines in LangChain en LangGraph voor classificatie, kwaliteitsbeoordeling, samenvatting en contactredenen, met evaluatie via LangSmith en analyses in BigQuery.",
    metrics: ["5000+ gesprekken per dag", "4 enterprise-klanten in jaar één"],
  },
  {
    client: "Clous Mode",
    title: "Een AI-platform van begin tot eind",
    body: "Leiding over de volledige ontwikkeling: architectuur, het samenstellen en aansturen van het team, resultaten en financiële grip.",
    metrics: ["Architectuur", "Team & delivery"],
  },
  {
    client: "GrandVision",
    title: "Betalingen en orders voor een wereldwijd e-commerceplatform",
    body: "Eindverantwoordelijk voor de kritieke keten van winkelwagen tot fulfilment op een SaaS-platform voor meer dan 25 operating companies. Een wereldwijde betaalarchitectuur met 4 betaalproviders, microservices achter Apollo Federation GraphQL en de koppeling met fulfilment via Boomi. Alles gebouwd als herbruikbare features, zonder maatwerk per land.",
    metrics: ["€100M+ jaaromzet", "25+ opco's", "30+ betaalmethoden"],
  },
  {
    client: "Booming Jobs",
    title: "Zoeken en matchen met taalmodellen",
    body: "De backend en de GCP-infrastructuur vanaf nul opgezet in Terraform. Daarna een zoek- en aanbevelingsengine gebouwd op BERT-embeddings, keyword-extractie en Typesense, en het engineeringteam geholpen groeien.",
    metrics: ["Platform vanaf nul", "Semantisch zoeken"],
  },
  {
    client: "Beecot",
    title: "Van nul naar een eigen engineeringteam",
    body: "Gestart zonder interne ontwikkelcapaciteit. We kozen de stack, bouwden de backend en de AWS-infrastructuur en zetten de standaarden neer. Daarna leidden we het interne team op en droegen we het platform aan hen over.",
    metrics: ["AWS & NestJS", "Kennisoverdracht"],
  },
  {
    client: "PIDZ",
    title: "Twee monolieten naar microservices",
    body: "Migratie van een PHP- en een Node.js-monoliet naar een cloud-native NestJS-platform op Kubernetes. We definieerden de architectuurpatronen en trainden ontwikkelaars in TypeScript, microservices en functioneel programmeren.",
    metrics: ["Kubernetes & GitOps", "Teamtraining"],
  },
];

export default function AppHome() {
  return (
    <div style={{ animation: "fadeUp .5s ease both" }}>
      <Helmet>
        <title>Datails — De technische partner die jouw platform bouwt én laat draaien</title>
        <meta
          name="description"
          content="Datails neemt volledige verantwoordelijkheid — van architectuur en cloudinfrastructuur tot AI-pipelines, backend en beheer. Ruim 11 jaar ervaring met bedrijfskritische platformen."
        />
      </Helmet>

      {/* Hero */}
      <section style={{ maxWidth: 1120, margin: "0 auto", padding: "96px 32px 72px" }}>
        <div style={{ maxWidth: 860 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
              padding: "7px 14px",
              border: `1px solid ${colors.borderStrong}`,
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.02em",
              color: colors.textMuted,
              marginBottom: 32,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#5C8A6A", display: "inline-block" }} />
            Jouw technische partner
          </div>
          <h1
            style={{
              fontFamily: serif,
              fontWeight: 400,
              fontSize: "clamp(44px,6.6vw,82px)",
              lineHeight: 1.02,
              letterSpacing: "-0.015em",
              color: colors.ink,
              textWrap: "balance",
            }}
          >
            De technische partner die jouw platform{" "}
            <span style={{ fontStyle: "italic", color: colors.teal }}>bouwt én laat draaien</span>.
          </h1>
          <p style={{ fontSize: "clamp(18px,2vw,21px)", lineHeight: 1.55, color: colors.text, marginTop: 30, maxWidth: 620 }}>
            Datails neemt volledige verantwoordelijkheid — van architectuur en cloudinfrastructuur tot AI-pipelines,
            backend en beheer. Met ruim 11 jaar ervaring in bedrijfskritische platformen. Zodat jij kunt focussen op je
            bedrijf.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 40 }}>
            <a
              href="mailto:info@datails.nl"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 9,
                background: colors.teal,
                color: "#FBF7EF",
                padding: "15px 26px",
                borderRadius: 999,
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Plan een kennismaking →
            </a>
            <button
              onClick={() => navigate("/diensten")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 9,
                background: "none",
                border: "1px solid #D3C9B6",
                color: colors.ink,
                padding: "15px 26px",
                borderRadius: 999,
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: sans,
              }}
            >
              Bekijk wat we doen
            </button>
          </div>
        </div>
        <div
          style={{
            marginTop: 64,
            paddingTop: 28,
            borderTop: `1px solid ${colors.border}`,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "12px 28px",
          }}
        >
          <span style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: colors.textFaint }}>
            Vertrouwd door teams bij
          </span>
          {clients.map((c, i) => (
            <React.Fragment key={c}>
              {i > 0 && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#C3BAA6" }} />}
              <span style={{ fontSize: 18, fontWeight: 600, color: "#3A4650" }}>{c}</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Cijfers */}
      <section style={{ maxWidth: 1120, margin: "0 auto", padding: "0 32px 80px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 32 }}>
          {stats.map((s) => (
            <div key={s.value} style={{ borderLeft: `2px solid ${colors.teal}`, paddingLeft: 18 }}>
              <div style={{ fontFamily: serif, fontSize: 38, lineHeight: 1.1, color: colors.ink }}>{s.value}</div>
              <p style={{ fontSize: 15, lineHeight: 1.5, color: colors.textMuted, marginTop: 8 }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Hoe we helpen */}
      <section style={{ background: colors.panel, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: "80px 32px" }}>
          <p style={eyebrow}>Hoe we helpen</p>
          <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: "clamp(32px,4vw,48px)", lineHeight: 1.08, letterSpacing: "-0.01em", maxWidth: 720 }}>
            Eén partner, van eerste schets tot draaiende productie.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 32, marginTop: 52 }}>
            {helpItems.map((item) => (
              <div key={item.n}>
                <div style={{ fontFamily: serif, fontSize: 34, color: colors.teal, marginBottom: 14 }}>{item.n}</div>
                <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 10 }}>{item.title}</h3>
                <p style={{ fontSize: 16, lineHeight: 1.55, color: colors.textMuted }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diensten preview */}
      <section style={{ maxWidth: 1120, margin: "0 auto", padding: "88px 32px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 20, marginBottom: 44 }}>
          <div>
            <p style={eyebrow}>Diensten</p>
            <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: "clamp(32px,4vw,48px)", lineHeight: 1.08, letterSpacing: "-0.01em" }}>
              Waarmee we je verder helpen
            </h2>
          </div>
          <button
            onClick={() => navigate("/diensten")}
            style={{ background: "none", border: "none", color: colors.teal, fontSize: 16, fontWeight: 600, cursor: "pointer", fontFamily: sans }}
          >
            Alle diensten →
          </button>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
            gap: 1,
            background: colors.border,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          {services.map((s) => (
            <div key={s.title} style={{ background: colors.cream, padding: "30px 28px" }}>
              <h3 style={{ fontSize: 19, fontWeight: 600, marginBottom: 9 }}>{s.title}</h3>
              <p style={{ fontSize: 15.5, lineHeight: 1.5, color: colors.textMuted }}>{s.body}</p>
            </div>
          ))}
          <div style={{ background: colors.panel, padding: "30px 28px", display: "flex", alignItems: "center" }}>
            <button
              onClick={() => navigate("/diensten")}
              style={{ background: "none", border: "none", color: colors.teal, fontSize: 16, fontWeight: 600, cursor: "pointer", fontFamily: sans, textAlign: "left" }}
            >
              Meer over onze aanpak →
            </button>
          </div>
        </div>
      </section>

      {/* In de praktijk */}
      <section style={{ background: colors.ink, color: "#EAF1EF" }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: "88px 32px" }}>
          <p style={{ fontSize: 14, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6FBFAD", marginBottom: 16 }}>
            In de praktijk
          </p>
          <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: "clamp(32px,4vw,48px)", lineHeight: 1.08, letterSpacing: "-0.01em", maxWidth: 720, marginBottom: 52 }}>
            Platformen die we bouwden en die nog elke dag draaien.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: "56px 48px" }}>
            {cases.map((c) => (
              <div key={c.client} style={{ borderTop: "1px solid #26323C", paddingTop: 26, display: "flex", flexDirection: "column" }}>
                <p style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "#6FBFAD", marginBottom: 12 }}>
                  {c.client}
                </p>
                <h3 style={{ fontFamily: serif, fontWeight: 400, fontSize: 28, lineHeight: 1.15, marginBottom: 14 }}>{c.title}</h3>
                <p style={{ fontSize: 16.5, lineHeight: 1.6, color: "#9DB0AE" }}>{c.body}</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 20 }}>
                  {c.metrics.map((m) => (
                    <span key={m} style={{ padding: "6px 12px", border: "1px solid #2E3D48", borderRadius: 999, fontSize: 13.5, fontWeight: 600, color: "#EAF1EF" }}>
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ maxWidth: 1120, margin: "0 auto", padding: "100px 32px", textAlign: "center" }}>
        <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: "clamp(34px,5vw,60px)", lineHeight: 1.05, letterSpacing: "-0.01em", maxWidth: 760, margin: "0 auto" }}>
          Klaar om te bouwen aan iets dat blijft draaien?
        </h2>
        <p style={{ fontSize: 19, lineHeight: 1.55, color: colors.textMuted, marginTop: 22, maxWidth: 520, marginLeft: "auto", marginRight: "auto" }}>
          Vertel ons over je project. We denken graag mee.
        </p>
        <a
          href="mailto:info@datails.nl"
          style={{ display: "inline-flex", alignItems: "center", gap: 9, background: colors.teal, color: "#FBF7EF", padding: "16px 30px", borderRadius: 999, fontSize: 17, fontWeight: 600, marginTop: 34 }}
        >
          info@datails.nl
        </a>
      </section>
    </div>
  );
}
